/* Scoped бүрхүүл — дизайны DesktopShell/Sidebar/MobileNav-аас (nav нь router болсон).
   Ээлжийн удирдлага: нээх/хаах товч header-т, intake нь зөвхөн нээлттэй ээлжид. */
import { createContext, useContext, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cwApi, type CwShiftReportDto } from '@/api/carwash';
import { NAV } from '@/shared/data';
import { tug } from '@/shared/format';
import { IconClock, IconDroplets, IconLogOut, IconPlus } from '@/shared/icons';
import { auth } from '@/shared/auth/storage';
import { useMediaQuery } from '@/shared/useMediaQuery';
import { publicRoutes, scopedRoutes, type ScopedRouteKey } from '@/app/routes';
import { TenantScopeProvider, useScopedNavigate, useTenantScope } from '@/app/providers/TenantScopeProvider';
import { useToast } from '@/app/providers/ToastProvider';
import { ShiftPromptDialog, ShiftReportModal } from '@/features/shift/ShiftDialogs';
import type { DayStats, NavKey } from '@/shared/types';

interface ShellUi {
  intakeOpen: boolean;
  setIntakeOpen: (v: boolean) => void;
  openIntake: () => void;
  isMobile: boolean;
  stats: DayStats;
  /* Ээлж */
  shiftOpen: boolean;
  shiftBusy: boolean;
  openShift: () => void;
  closeShift: () => void;
}

const ShellCtx = createContext<ShellUi | null>(null);

export function useShellUi() {
  const c = useContext(ShellCtx);
  if (!c) throw new Error('useShellUi must be used within ShellLayout');
  return c;
}

function activeNav(pathname: string): NavKey {
  const seg = pathname.split('/').filter(Boolean)[2] ?? 'queue';
  return (Object.values(scopedRoutes) as string[]).includes(seg) ? (seg as NavKey) : 'queue';
}

function shiftTimeText(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function DesktopHeader({ stats, onIntake, shiftOpenedAt, shiftBusy, onOpenShift, onCloseShift }: {
  stats: DayStats;
  onIntake: () => void;
  shiftOpenedAt: string | null;
  shiftBusy: boolean;
  onOpenShift: () => void;
  onCloseShift: () => void;
}) {
  const scope = useTenantScope();
  const routerNav = useNavigate();
  const qc = useQueryClient();
  const onLogout = () => {
    auth.clear();
    qc.clear();
    routerNav(publicRoutes.login, { replace: true });
  };
  return (
    <header className="flex items-center gap-4 px-6 py-3 bg-white border-b border-slate-200">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-[12px] bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] flex items-center justify-center text-white shadow-xs">
          <IconDroplets size={20} strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <div className="font-extrabold text-slate-900 text-[15px] tracking-tight">Carwash</div>
          <div className="text-[11px] text-slate-400 font-medium">{scope.companyName || 'tsetsu.mn'}</div>
        </div>
      </div>

      <div className="h-8 w-px bg-slate-200"></div>

      <div className="text-sm text-slate-600">
        Өнөөдөр: <span className="font-bold text-slate-900">{stats.cars} машин</span>
        <span className="text-slate-300"> · </span>
        <span className="font-bold text-slate-900">{tug(stats.revenue)}</span>
      </div>

      <div className="ml-auto flex items-center gap-3">
        {/* Ээлжийн төлөв + нээх/хаах */}
        {shiftOpenedAt ? (
          <span className="inline-flex items-center gap-2 text-[13px] font-medium text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full pl-3 pr-1.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Ээлж · {shiftTimeText(shiftOpenedAt)}-аас
            <button onClick={onCloseShift} disabled={shiftBusy}
              className="min-h-[30px] px-2.5 rounded-full text-[12px] font-semibold text-rose-600 bg-white ring-1 ring-rose-200 hover:bg-rose-50 transition disabled:opacity-60">
              {shiftBusy ? 'Хааж байна…' : 'Ээлж хаах'}
            </button>
          </span>
        ) : (
          <button onClick={onOpenShift} disabled={shiftBusy}
            className="inline-flex items-center gap-1.5 min-h-[38px] px-4 rounded-full text-[13px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 hover:bg-emerald-100 transition disabled:opacity-60">
            <IconClock size={15} strokeWidth={2.2} />
            {shiftBusy ? 'Нээж байна…' : 'Ээлж нээх'}
          </button>
        )}

        <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 bg-slate-50 ring-1 ring-slate-200 rounded-full px-3 py-1.5">
          {auth.displayName || 'Кассчин'}
        </span>
        <button onClick={onIntake} className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-[var(--r)] text-white text-sm font-semibold bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95 active:scale-[0.98] transition">
          <IconPlus size={18} strokeWidth={2.5} />
          Машин хүлээн авах
        </button>
        <button onClick={onLogout} title="Системээс гарах"
          className="w-10 h-10 rounded-[10px] flex items-center justify-center text-slate-400 ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200 transition">
          <IconLogOut size={17} strokeWidth={2.2} />
        </button>
      </div>
    </header>
  );
}

function Sidebar({ active, onNav }: { active: NavKey; onNav: (k: NavKey) => void }) {
  return (
    <nav className="w-[76px] shrink-0 bg-white border-r border-slate-200 flex flex-col items-center py-4 gap-1">
      {NAV.map(({ key, label, Icon }) => (
        <button key={key} onClick={() => onNav(key)} className={
          'w-[60px] py-2.5 rounded-[12px] flex flex-col items-center gap-1 text-[10px] font-semibold transition ' +
          (key === active ? 'bg-cyan-50 text-[var(--brand1)]' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600')
        }>
          <Icon size={21} strokeWidth={key === active ? 2.4 : 2} />
          {label}
        </button>
      ))}
    </nav>
  );
}

function MobileNav({ active, onNav }: { active: NavKey; onNav: (k: NavKey) => void }) {
  return (
    <nav className="absolute bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-5 px-2 pt-1.5 pb-3 z-20">
      {NAV.map(({ key, label, Icon }) => (
        <button key={key} onClick={() => onNav(key)} className={
          'min-h-[48px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold rounded-[10px] transition ' +
          (key === active ? 'text-[var(--brand1)]' : 'text-slate-400')
        }>
          <Icon size={21} strokeWidth={key === active ? 2.4 : 2} />
          {label}
        </button>
      ))}
    </nav>
  );
}

function ShellInner() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const location = useLocation();
  const nav = useScopedNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [shiftPrompt, setShiftPrompt] = useState(false);
  const [shiftReport, setShiftReport] = useState<CwShiftReportDto | null>(null);
  const active = activeNav(location.pathname);

  const todayQ = useQuery({
    queryKey: ['cw', 'today'],
    queryFn: cwApi.today,
    refetchInterval: 30_000,
  });
  const stats: DayStats = {
    cars: todayQ.data?.Cars ?? 0,
    revenue: todayQ.data?.Revenue ?? 0,
  };

  const shiftQ = useQuery({
    queryKey: ['cw', 'shift'],
    queryFn: cwApi.shiftCurrent,
    refetchInterval: 30_000,
  });
  const openShiftDto = shiftQ.data?.Shift ?? null;
  const shiftOpen = !!openShiftDto;

  const invalidateShift = () => {
    qc.invalidateQueries({ queryKey: ['cw', 'shift'] });
    qc.invalidateQueries({ queryKey: ['cw', 'shift-list'] });
    qc.invalidateQueries({ queryKey: ['cw', 'today'] });
  };
  const onError = (e: unknown) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.');

  const openMut = useMutation({
    mutationFn: cwApi.shiftOpen,
    onSuccess: () => {
      invalidateShift();
      toast('Ээлж нээгдлээ');
      /* Popup-аас нээсэн бол шууд intake руу үргэлжилнэ */
      if (shiftPrompt) {
        setShiftPrompt(false);
        if (active !== 'queue') nav('queue');
        setIntakeOpen(true);
      }
    },
    onError,
  });

  const closeMut = useMutation({
    mutationFn: cwApi.shiftClose,
    onSuccess: (report) => {
      invalidateShift();
      setShiftReport(report);
    },
    onError,
  });

  const shiftBusy = openMut.isPending || closeMut.isPending;

  const openIntake = () => {
    if (!shiftOpen) {
      setShiftPrompt(true);
      return;
    }
    if (active !== 'queue') nav('queue');
    setIntakeOpen(true);
  };
  const onNav = (k: NavKey) => nav(k as ScopedRouteKey);

  const ui: ShellUi = {
    intakeOpen, setIntakeOpen, openIntake, isMobile, stats,
    shiftOpen, shiftBusy,
    openShift: () => openMut.mutate(),
    closeShift: () => closeMut.mutate(),
  };

  const dialogs = (
    <>
      <ShiftPromptDialog open={shiftPrompt} busy={openMut.isPending}
        onOpenShift={() => openMut.mutate()} onClose={() => setShiftPrompt(false)} />
      <ShiftReportModal report={shiftReport} onClose={() => setShiftReport(null)} />
    </>
  );

  return (
    <ShellCtx.Provider value={ui}>
      <div className="h-screen w-screen overflow-hidden bg-slate-100 font-sans">
        <div className="relative h-full">
          {isMobile ? (
            <div className="relative h-full bg-slate-50">
              <Outlet />
              <MobileNav active={active} onNav={onNav} />
              {dialogs}
            </div>
          ) : (
            <div className="h-full flex flex-col bg-slate-50">
              <DesktopHeader stats={stats} onIntake={openIntake}
                shiftOpenedAt={openShiftDto?.OpenedAt ?? null} shiftBusy={shiftBusy}
                onOpenShift={() => openMut.mutate()} onCloseShift={() => closeMut.mutate()} />
              <div className="flex flex-1 min-h-0">
                <Sidebar active={active} onNav={onNav} />
                <main className="flex-1 min-w-0 min-h-0 flex flex-col">
                  <Outlet />
                </main>
              </div>
              {dialogs}
            </div>
          )}
        </div>
      </div>
    </ShellCtx.Provider>
  );
}

export function ShellLayout() {
  if (!auth.isAuthed) return <Navigate to={publicRoutes.login} replace />;
  return (
    <TenantScopeProvider>
      <ShellInner />
    </TenantScopeProvider>
  );
}
